import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { defaultPageContent, mergePageContent, preparePageContentForSave } from '../utils/pageContentDefaults';
import {
  readBootstrapBrandingFromDocument,
  readCachedPublicSettings,
  writeCachedPublicSettings
} from '../utils/settingsCache';
import { mergeThemeColors } from '../utils/themeDefaults';

// Create the Settings context
export const SettingsContext = createContext();

// Custom hook to use the settings context
export const useSettings = () => useContext(SettingsContext);

const upsertMetaTag = (attrName, attrValue, content) => {
  if (!content) return;

  let tag = document.querySelector(`meta[${attrName}="${attrValue}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attrName, attrValue);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const upsertLinkTag = (rel, href) => {
  if (!href) return;

  let tag = document.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
};

const buildUploadUrl = (uploadsUrl, filePath) => {
  if (!filePath) return '';
  const cleanPath = filePath.replace(/^\/?(uploads\/)+/, '');
  return `${uploadsUrl}/${cleanPath}`;
};

const normalizeSiteUrl = (rawUrl) => {
  const trimmed = String(rawUrl || '').trim();
  if (!trimmed) return '';

  let url = trimmed;
  if (!/^https?:\/\//i.test(url)) {
    url = `https://${url}`;
  }

  return url.replace(/^http:\/\//i, 'https://').replace(/\/$/, '');
};

const mapSettingsResponse = (data) => ({
  siteName: data.siteName || 'Musician Portfolio',
  siteDescription: data.siteDescription || 'Professional musician portfolio website',
  siteUrl: data.siteUrl || 'https://justinmirandaguitar.com',
  favicon: data.favicon || '',
  socialImage: data.socialImage || '',
  contactEmail: data.contactEmail || '',
  defaultLanguage: data.defaultLanguage || 'en',
  timeZone: data.timeZone || 'UTC',
  enableBookings: data.enableBookings !== undefined ? data.enableBookings : true,
  enableLessons: data.enableLessons !== undefined ? data.enableLessons : true,
  maxUploadSize: data.maxUploadSize || 10,
  maintenanceMode: data.maintenanceMode || false,
  pageContent: mergePageContent(data.pageContent),
  themeColors: mergeThemeColors(data.themeColors)
});

const buildDefaultSettings = () => ({
  siteName: 'Musician Portfolio',
  siteDescription: 'Professional musician portfolio website',
  siteUrl: 'https://justinmirandaguitar.com',
  favicon: '',
  socialImage: '',
  contactEmail: '',
  defaultLanguage: 'en',
  timeZone: 'UTC',
  enableBookings: true,
  enableLessons: true,
  maxUploadSize: 10,
  maintenanceMode: false,
  pageContent: defaultPageContent,
  themeColors: mergeThemeColors()
});

const buildInitialSettings = () => {
  const cached = readCachedPublicSettings();
  if (cached) {
    return mapSettingsResponse(cached);
  }

  const boot = readBootstrapBrandingFromDocument();
  if (boot) {
    return {
      ...buildDefaultSettings(),
      ...boot
    };
  }

  return buildDefaultSettings();
};

// Settings Provider component
export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(buildInitialSettings);
  const [loading, setLoading] = useState(() => !readCachedPublicSettings());
  const [error, setError] = useState(null);
  
  const apiUrl = process.env.REACT_APP_API_URL || '/api';
  const uploadsUrl = process.env.REACT_APP_UPLOADS_URL || '/uploads';
  
  // Fetch settings on component mount
  useEffect(() => {
    const fetchSettings = async () => {
      const hasCachedSettings = !!readCachedPublicSettings();

      try {
        if (!hasCachedSettings) {
          setLoading(true);
        }

        const res = await axios.get(`${apiUrl}/settings`);
        
        if (res.data) {
          setSettings(mapSettingsResponse(res.data));
          writeCachedPublicSettings(res.data);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching settings:', err);
        setError('Failed to load settings');
        setLoading(false);
      }
    };

    fetchSettings();
  }, [apiUrl]);
  
  // Keep browser-facing branding in sync with admin settings.
  useEffect(() => {
    if (!settings.siteName) return;

    document.title = settings.siteName;
    upsertMetaTag('name', 'description', settings.siteDescription);
    upsertMetaTag('property', 'og:title', settings.siteName);
    upsertMetaTag('property', 'og:description', settings.siteDescription);
    upsertMetaTag('property', 'og:site_name', settings.siteName);
    upsertMetaTag('name', 'twitter:title', settings.siteName);
    upsertMetaTag('name', 'twitter:description', settings.siteDescription);
    upsertMetaTag('name', 'apple-mobile-web-app-title', settings.siteName);

    const baseUrl = normalizeSiteUrl(settings.siteUrl) || window.location.origin.replace(/^http:\/\//i, 'https://');
    if (baseUrl) {
      upsertMetaTag('property', 'og:url', `${baseUrl}/`);
    }

    const faviconUrl = buildUploadUrl(uploadsUrl, settings.favicon);
    if (faviconUrl) {
      upsertLinkTag('icon', faviconUrl);
      upsertLinkTag('apple-touch-icon', faviconUrl);
    }

    const socialImageUrl = buildUploadUrl(uploadsUrl, settings.socialImage);
    if (socialImageUrl) {
      const absoluteSocialImage = settings.siteUrl
        ? `${baseUrl}/uploads/${settings.socialImage.replace(/^\/?(uploads\/)+/, '')}`
        : `${window.location.origin.replace(/^http:\/\//i, 'https://')}${socialImageUrl}`;
      upsertMetaTag('property', 'og:image', absoluteSocialImage);
      upsertMetaTag('name', 'twitter:image', absoluteSocialImage);
    }
  }, [settings, uploadsUrl]);

  const refreshSettings = async () => {
    try {
      const res = await axios.get(`${apiUrl}/settings`);

      if (res.data) {
        setSettings(mapSettingsResponse(res.data));
        writeCachedPublicSettings(res.data);
      }

      setError(null);
    } catch (err) {
      console.error('Error refreshing settings:', err);
      setError('Failed to refresh settings');
    }
  };

  const updatePageContent = async (nextPageContent) => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('You must be logged in as an admin to edit page content.');
    }

    const res = await axios.post(
      `${apiUrl}/settings`,
      { pageContent: preparePageContentForSave(nextPageContent) },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (res.data?.settings) {
      setSettings(mapSettingsResponse(res.data.settings));
      writeCachedPublicSettings(res.data.settings);
    } else {
      await refreshSettings();
    }
  };

  const contextValue = {
    settings,
    pageContent: settings.pageContent,
    loading,
    error,
    updatePageContent,
    refreshSettings
  };
  
  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};
